import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import uuidv4 from 'uuid/v4';

import { differenceBy } from 'lodash';
import client from '../../../utils/apolloClient';
import { CheckBox, Loading } from '../../../components/elements';

const Container = styled.div`
  &&& {
    .modal-background {
      background-color: rgba(13, 13, 13, 0.28);
    }
    .modal-card-body {
      height: 100vh;
      padding: 0px;
    }
    .modal-card {
      max-height: calc(100vh - 20%);
      position: absolute;
      width: 60%;
    }
    .modal-card-head,
    .modal-card-foot {
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
    }
    .modal-card-title {
      font-size: 1.5rem;
      font-weight: 600;
    }
    .modal-card-head {
      border-bottom: 0px;
      padding: 1.5rem 1rem;
      background: white;
    }
    .modal-card-foot {
      justify-content: flex-end;
      border: 0px;
      padding: 1rem;
      background: white;
    }
    a {
      font-size: 1rem;
    }
  }
`;

const SecondaryHeader = styled.div`
  background: white;
`;
const InputWrapper = styled.div`
  padding: 1rem;
  width: 50%;
`;

const EmptyMessage = styled.div`
  padding: 1rem;
`;

const searchMenuItemsQuery = gql`
  query searchSettings($input: SearchInput) {
    search_menu_items(input: $input) {
      menu_item_listing {
        selling_type
        internal_name
        menu_item_id
        place_id
        name
        price
        availability_status
        modifier_groups {
          name
        }
        variant {
          prompt_id
          prompt
          variants {
            variant_id
            availability_status
            deliverect_plu_id
            variant_name
            variant_price
          }
        }
      }
    }
  }
`;

const SearchItemModal = ({
  placeId,
  onSubmit,
  isActive,
  onClose,
  initialsItems,
  isCreateLinkLoading,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const fetchItems = value => {
    const menu_item_filter = {};
    Object.assign(
      menu_item_filter,
      { place_id: placeId },
      value && { name: value },
      { status: 'ACTIVE' },
    );
    setLoading(true);
    client.clientPublic
      .query({
        query: searchMenuItemsQuery,
        variables: { input: { size: 200, filter: { menu_item_filter } } },
      })
      .then(({ data }) => {
        if (
          data.search_menu_items &&
          data.search_menu_items.menu_item_listing
        ) {
          const menuItems = data.search_menu_items.menu_item_listing;
          const menuItemsWithoutInitialItems = differenceBy(
            menuItems,
            initialsItems,
            'menu_item_id',
          );
          const finalMenuItems = menuItemsWithoutInitialItems
            .filter(
              menuItem =>
                menuItem.selling_type === 'ITEM' ||
                menuItem.selling_type === 'DEAL',
            )
            .map(menuItem => {
              const { selling_type: _, ...newMenuItem } = menuItem;
              return newMenuItem;
            });
          setItems(finalMenuItems);
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onSearch = value => {
    fetchItems(value);
  };

  useEffect(() => {
    fetchItems();
    setSelectedItems([]);
  }, [initialsItems]);

  const addItem = item => {
    setSelectedItems(selectedItems.concat({ ...item, item_link_id: uuidv4() }));
  };

  const removeItem = id => {
    setSelectedItems(selectedItems.filter(item => item.menu_item_id !== id));
  };

  return (
    <Container className={`modal ${isActive && 'is-active'}`}>
      <div className="modal-background" />
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">Add Items</p>
          <button
            type="button"
            className="delete"
            aria-label="close"
            onClick={onClose}
          />
        </header>
        <SecondaryHeader>
          <InputWrapper>
            <div className="field">
              <p className="control has-icons-left has-icons-right">
                <input
                  className="input"
                  type="search"
                  placeholder="Search menu items..."
                  onChange={e => onSearch(e.target.value)}
                />
                <span className="icon is-small is-left">
                  <i className="fas fa-search" />
                </span>
              </p>
            </div>
          </InputWrapper>
        </SecondaryHeader>

        <section className="modal-card-body">
          <table className="table is-fullwidth">
            <thead>
              <tr>
                <th />
                <th>
                  <abbr>Item Name</abbr>
                </th>
                <th>
                  <abbr>Menus</abbr>
                </th>
                <th>
                  <abbr>Categories</abbr>
                </th>
                <th>
                  <abbr>Modifiers</abbr>
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                items.map(item => (
                  <tr>
                    <td>
                      <CheckBox
                        value={
                          selectedItems.filter(
                            edge => edge.menu_item_id === item.menu_item_id,
                          ).length !== 0
                        }
                        onChange={value =>
                          value
                            ? addItem({ id: uuidv4(), ...item })
                            : removeItem(item.menu_item_id)
                        }
                      />
                    </td>
                    <td>
                      {item.name}{' '}
                      <span style={{ fontSize: '10px', color: '#505050' }}>
                        ({item.internal_name})
                      </span>
                    </td>
                    <td>-</td>
                    <td>-</td>
                    <td>
                      {item?.modifier_groups?.map(item2 => `${item2.name}, `)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {loading && <Loading />}
          {!loading && items.length === 0 && (
            <EmptyMessage>
              <p>No Items Found</p>
            </EmptyMessage>
          )}
        </section>
        <footer className="modal-card-foot">
          <button
            type="button"
            className="button is-grey-light"
            aria-label="close"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`button is-primary ${isCreateLinkLoading &&
              'is-loading'}`}
            aria-label="close"
            onClick={() => {
              onSubmit(selectedItems);
            }}
            disabled={selectedItems.length === 0}
          >
            <p className="has-text-weight-semibold">Add Item(s)</p>
          </button>
        </footer>
      </div>
    </Container>
  );
};

export default SearchItemModal;
